import type { ModeDefinition } from './types';

export const daySkyMode: ModeDefinition = {
  id: 'day-sky',
  name: 'Day Sky',
  modeIndex: 1,
  groups: [
    { id: 'sun', label: 'Sun', collapsible: true, collapsedByDefault: true },
    { id: 'clouds', label: 'Clouds', collapsible: true, collapsedByDefault: true },
    { id: 'tone', label: 'Tone Mapping', collapsible: true, collapsedByDefault: true },
  ],
  params: [
    // Sun
    {
      id: 'sunEnabled',
      type: 'toggle',
      label: 'Enabled',
      group: 'sun',
      default: true,
      tooltip: 'Show sun in the sky',
    },
    {
      id: 'sunCount',
      type: 'slider',
      label: 'Count',
      group: 'sun',
      tooltip: 'Number of suns (for alien worlds)',
      min: 1,
      max: 4,
      step: 1,
      default: 1,
    },
    {
      id: 'sunSizeMin',
      type: 'slider',
      label: 'Size Min',
      group: 'sun',
      tooltip: 'Minimum size of sun discs',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.4,
    },
    {
      id: 'sunSizeMax',
      type: 'slider',
      label: 'Size Max',
      group: 'sun',
      tooltip: 'Maximum size of sun discs',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.6,
    },
    {
      id: 'sunIntensity',
      type: 'slider',
      label: 'Brightness',
      group: 'sun',
      tooltip: 'Brightness of the sun',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.7,
    },

    // Clouds
    {
      id: 'nebula',  // Reused as cloud intensity in day mode
      type: 'slider',
      label: 'Cloud Intensity',
      group: 'clouds',
      tooltip: 'Brightness and density of clouds',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.5,
    },
    {
      id: 'coverage',
      type: 'slider',
      label: 'Cloud Coverage',
      group: 'clouds',
      tooltip: 'How much of the sky has clouds',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.4,
    },
    {
      id: 'detail',
      type: 'slider',
      label: 'Cloud Detail',
      group: 'clouds',
      tooltip: 'Amount of fine detail in cloud shapes',
      min: 0.2,
      max: 1.5,
      step: 0.01,
      default: 0.8,
    },

    // Tone mapping
    {
      id: 'contrast',
      type: 'slider',
      label: 'Contrast',
      group: 'tone',
      tooltip: 'Boosts overall contrast',
      min: 0.8,
      max: 1.4,
      step: 0.01,
      default: 1.0,
      lockedByDefault: true,
    },
    {
      id: 'gamma',
      type: 'slider',
      label: 'Gamma',
      group: 'tone',
      tooltip: 'Adjusts midtone brightness',
      min: 0.6,
      max: 1.3,
      step: 0.01,
      default: 1.0,
      lockedByDefault: true,
    },
  ],

  randomize(currentValues, lockedParams) {
    const values = { ...currentValues };

    // Randomize sun
    if (!lockedParams.has('sunCount')) {
      values.sunCount = Math.random() < 0.15 ? Math.floor(2 + Math.random() * 2) : 1;
    }
    if (!lockedParams.has('sunSizeMin')) {
      values.sunSizeMin = 0.2 + Math.random() * 0.4;
    }
    if (!lockedParams.has('sunSizeMax')) {
      values.sunSizeMax = Math.max((values.sunSizeMin as number) + 0.1, 0.4 + Math.random() * 0.4);
    }
    if (!lockedParams.has('sunIntensity')) {
      values.sunIntensity = 0.5 + Math.random() * 0.4;
    }

    // Randomize clouds
    if (!lockedParams.has('nebula')) {
      values.nebula = 0.3 + Math.random() * 0.5;
    }
    if (!lockedParams.has('coverage')) {
      values.coverage = 0.1 + Math.random() * 0.6;
    }
    if (!lockedParams.has('detail')) {
      values.detail = 0.5 + Math.random() * 0.8;
    }
    if (!lockedParams.has('contrast')) {
      values.contrast = 0.9 + Math.random() * 0.2;
    }
    if (!lockedParams.has('gamma')) {
      values.gamma = 0.9 + Math.random() * 0.2;
    }

    return values;
  },
};
